import { useState, useRef, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const PHONE_NUMBER = process.env.REACT_APP_BRAND_PHONE || '+32 497 780 007';
const WHATSAPP_NUMBER = (PHONE_NUMBER || '').replace(/\D/g, '');
const PROACTIVE_DELAY_MS = 30000;
const PROACTIVE_AUTO_DISMISS_MS = 12000;

export const useChatLogic = ({ language, labels }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isHumanTakeover, setIsHumanTakeover] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  
  const [customerName, setCustomerName] = useState('');
  const [contactInfo, setContactInfo] = useState(''); 
  
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showProactive, setShowProactive] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgTimeRef = useRef(null);

  const playNotification = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (sessionStorage.getItem('fedyk-proactive-shown')) return undefined;
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowProactive(true);
        sessionStorage.setItem('fedyk-proactive-shown', '1');
        setTimeout(() => setShowProactive(false), PROACTIVE_AUTO_DISMISS_MS);
      }
    }, PROACTIVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // ── LONG POLLING ──
  useEffect(() => {
    if (!isOpen) {
      clearTimeout(pollRef.current);
      return;
    }
    let isMounted = true;
    const poll = async () => {
      try {
        const after = lastMsgTimeRef.current ? `?after=${encodeURIComponent(lastMsgTimeRef.current)}` : '';
        const res = await fetch(`${API}/api/chat/new-messages/${sessionId}${after}`);
        if (!res.ok) throw new Error('Polling error');
        const data = await res.json();

        if (isMounted) {
          // ✅ ИСПРАВЛЕНИЕ 1: Теперь мы реагируем как на включение (true), так и на отключение (false) менеджера
          if (typeof data.is_human_takeover !== 'undefined') {
            setIsHumanTakeover(data.is_human_takeover);
          }

          const newMsgs = data.messages || [];
          if (newMsgs.length > 0) {
            setMessages((prev) => {
              const existingText = new Set(prev.map((p) => p.content.trim().toLowerCase()));
              const trulyNew = newMsgs.filter((m) => !existingText.has(m.content.trim().toLowerCase()));
              
              if (trulyNew.length === 0) return prev;
              
              lastMsgTimeRef.current = trulyNew[trulyNew.length - 1].created_at;
              playNotification();
              
              return [
                ...prev,
                ...trulyNew.map((m) => ({
                  id: m.id || crypto.randomUUID(),
                  // ✅ ИСПРАВЛЕНИЕ 2: Оставляем оригинальную роль (включая 'system'), если она есть
                  role: m.role || (data.is_human_takeover ? 'manager' : 'assistant'),
                  content: m.content,
                  timestamp: new Date(m.created_at)
                })),
              ];
            });
          }
        }
      } catch (err) {} finally {
        if (isMounted && isOpen) pollRef.current = setTimeout(poll, 1500);
      }
    };
    poll();
    return () => {
      isMounted = false;
      clearTimeout(pollRef.current);
    };
  }, [isOpen, sessionId, playNotification]);

  useEffect(() => {
    if (!(isOpen && nameConfirmed)) return;
    if (!hasGreeted) {
      setMessages([{ id: crypto.randomUUID(), role: 'assistant', content: labels.greeting, timestamp: new Date() }]);
      setHasGreeted(true);
    }
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(focusTimer);
  }, [isOpen, hasGreeted, nameConfirmed, labels.greeting]);

  const confirmName = useCallback(() => {
    if (!customerName.trim()) return;
    setNameConfirmed(true);
  }, [customerName]);

  const sendMessage = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    const userMsg = { id: crypto.randomUUID(), role: 'user', content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            session_id: sessionId, 
            message: text, 
            language, 
            customer_name: customerName,
            contact_info: contactInfo 
        }),
      });
      if (!res.ok) throw new Error('Chat failed');
      const data = await res.json();
      
      // ✅ ИСПРАВЛЕНИЕ 3: При отправке сообщения тоже проверяем, не отключился ли менеджер
      if (typeof data.is_human_takeover !== 'undefined') {
        setIsHumanTakeover(data.is_human_takeover);
      }
      
      if (data.response && !data.is_human_takeover) {
        setMessages((prev) => {
           if (prev.some(p => p.content.trim().toLowerCase() === data.response.trim().toLowerCase())) return prev;
           return [...prev, { id: crypto.randomUUID(), role: 'assistant', content: data.response, timestamp: new Date() }];
        });
        lastMsgTimeRef.current = new Date().toISOString();
      }
    } catch (error) {
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(), role: 'assistant', content: `Sorry, something went wrong. Call us at ${PHONE_NUMBER}.`, timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, sessionId, language, customerName, contactInfo]);

  const requestHuman = useCallback(async () => {
    try {
      await fetch(`${API}/api/chat/request-human/${sessionId}`, { method: 'POST' });
      setIsHumanTakeover(true);
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'system', content: labels.humanRequested, timestamp: new Date() }]);
    } catch (error) {}
  }, [sessionId, labels.humanRequested]);

  const openWhatsApp = useCallback(() => {
    const greeting = encodeURIComponent(`Hi, I'm ${customerName || 'interested in your products'}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${greeting}`, '_blank');
  }, [customerName]);

  return {
    isOpen, messages, inputValue, isLoading, isHumanTakeover,
    sessionId, customerName, contactInfo, nameConfirmed, showProactive,
    phoneNumber: PHONE_NUMBER, messagesEndRef, inputRef,
    setIsOpen, setInputValue, setCustomerName, setContactInfo, setShowProactive,
    confirmName, sendMessage, requestHuman, openWhatsApp,
  };
};

export default useChatLogic;
